import {AuthenticationError, type ServerAuthenticationEntryPoint} from '../types.js';
import {Oauth2AuthenticationError} from './token-converter.js';
import type {BearerTokenError, OAuth2Error} from './token-error.js';
import {HttpStatus} from '../../../http/status.js';

function computeWWWAuthenticate(parameters: Map<string, string>) {
    let wwwAuthenticate = 'Bearer';
    if (parameters.size !== 0) {
        wwwAuthenticate += ' ';
        let i = 0;
        for (const [key, value] of parameters) {
            wwwAuthenticate += `${key}="${value}"`;
            if (i !== parameters.size - 1) {
                wwwAuthenticate += ', ';
            }
            i++;
        }
    }
    return wwwAuthenticate;
}

const isBearerTokenError = (error: OAuth2Error): error is BearerTokenError => {
    return (error as BearerTokenError).httpStatus !== undefined;
}

function getStatus(authError: AuthenticationError): HttpStatus {
    if (authError instanceof Oauth2AuthenticationError) {
        const {error} = authError;
        if (isBearerTokenError(error)) {
            return error.httpStatus;
        }
    }
    return HttpStatus.UNAUTHORIZED;
}

function createParameters(authError: AuthenticationError, realm?: string) {
    const parameters = new Map<string, string>();
    if (realm) {
        parameters.set('realm', realm);
    }
    if (authError instanceof Oauth2AuthenticationError) {
        const {error} = authError;
        parameters.set('error', error.errorCode);
        if (error.description) {
            parameters.set('error_description', error.description);
        }
        if (error.uri) {
            parameters.set('error_uri',  error.uri);
        }
        if (isBearerTokenError(error) && error.scope) {
            parameters.set('scope', error.scope);
        }
    }
    return parameters;
}

const bearerTokenServerAuthenticationEntryPoint = (opts?: { realmName?: string }): ServerAuthenticationEntryPoint => {
    return async (exchange, error) => {
        const status = getStatus(error);
        const parameters = createParameters(error, opts?.realmName)
        const wwwAuthenticate = computeWWWAuthenticate(parameters);
        const {response} = exchange;
        response.headers.set('WWW-Authenticate', wwwAuthenticate);
        response.setStatusCode(status);
        await response.end();
    };
}

export default bearerTokenServerAuthenticationEntryPoint;
